import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Input from '../forms/Input';
import Button from '../forms/Button';
import { Link, X, Trash2 } from 'lucide-react';

const LinkModal = ({ isOpen, onClose, onSubmit, onRemove, selectedText, existingLink }) => {
  const [url, setUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      // Initialize form values
      if (existingLink) {
        setUrl(existingLink);
        setLinkText(selectedText || existingLink);
      } else {
        setUrl('https://');
        setLinkText(selectedText || '');
      }
      setError('');
    }
  }, [isOpen, existingLink, selectedText]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!url || !url.trim()) {
      setError('Please enter a URL');
      return;
    }

    // Validate and format URL
    let formattedUrl = url.trim();
    if (!formattedUrl.match(/^https?:\/\//i)) {
      formattedUrl = 'https://' + formattedUrl;
    }

    // Basic URL validation
    try {
      new URL(formattedUrl);
    } catch {
      setError('Please enter a valid URL');
      return;
    }

    onSubmit(formattedUrl, linkText.trim() || formattedUrl);
    handleClose();
  };

  const handleClose = () => {
    setUrl('');
    setLinkText('');
    setError('');
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  const content = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999]"
          />

          {/* Modal */}
          <div
            className="fixed inset-0 flex items-center justify-center p-4 z-[10000] pointer-events-none"
            onKeyDown={handleKeyDown}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
              transition={{
                type: 'spring',
                damping: 25,
                stiffness: 300,
              }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-primary-card rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-dark-100 pointer-events-auto"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-dark-100">
                <div className="flex items-center gap-2">
                  <Link className="w-5 h-5 text-primary-accent" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-light-100">
                    {existingLink ? 'Edit Link' : 'Add Link'}
                  </h3>
                </div>
                <button
                  onClick={handleClose}
                  className="text-gray-500 dark:text-light-500 hover:text-gray-700 dark:hover:text-light-100 transition-colors p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                <Input
                  label="URL"
                  type="url"
                  value={url}
                  onChange={(e) => {
                    setUrl(e.target.value);
                    setError('');
                  }}
                  placeholder="https://example.com"
                  error={error}
                  autoFocus
                  required
                  icon={<Link className="w-4 h-4" />}
                />

                {!existingLink && (
                  <div>
                    <Input
                      label="Link Text (optional)"
                      type="text"
                      value={linkText}
                      onChange={(e) => setLinkText(e.target.value)}
                      placeholder={selectedText || 'Enter link text'}
                    />
                    {selectedText && (
                      <p className="text-xs text-gray-500 dark:text-light-500 mt-1">
                        Text is selected in editor
                      </p>
                    )}
                    {!selectedText && (
                      <p className="text-xs text-gray-500 dark:text-light-500 mt-1">
                        Leave empty to use URL as text
                      </p>
                    )}
                  </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between pt-2">
                  {existingLink && onRemove && (
                    <Button
                      type="button"
                      variant="danger"
                      onClick={() => {
                        onRemove();
                        handleClose();
                      }}
                      size="sm"
                      icon={<Trash2 className="w-4 h-4" />}
                    >
                      Remove Link
                    </Button>
                  )}
                  <div className={`flex items-center gap-3 ${existingLink && onRemove ? 'ml-auto' : ''}`}>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={handleClose}
                      size="sm"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      size="sm"
                      icon={<Link className="w-4 h-4" />}
                    >
                      {existingLink ? 'Update Link' : 'Add Link'}
                    </Button>
                  </div>
                </div>
              </form>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(content, document.body);
};

export default LinkModal;
